/**
 * @mountayaapp/docs OpenAPI reference outline. Redoc renders the whole document
 * eagerly, tagging every tag group and every operation with a `data-section-id`
 * that is also the element's `id` and the hash it deep-links to. Reading the
 * outline back off that markup keeps the navigation rail in step with what the
 * reference actually rendered, which re-parsing the specification could not
 * promise: the anchors are Redoc's own, so a rail entry can never point at a
 * section that is not there.
 */

/**
 * SpecOperation is one operation in the rail: the section id it scrolls to, the
 * summary Redoc renders as its heading, and the HTTP method its badge carries.
 */
export interface SpecOperation {
  id: string;
  label: string;
  method: string;
}

/**
 * SpecSection is one tag group in the rail: its section id, the tag name, and the
 * operations filed under it in specification order.
 */
export interface SpecSection {
  id: string;
  label: string;
  operations: SpecOperation[];
}

/**
 * TAG_ID matches a tag group's section id (`tag/Sessions`) and OPERATION_ID an
 * operation's (`tag/Sessions/operation/postSession`). Redoc also emits a legacy
 * un-tagged alias for each operation (`operation/postSession`) as its own
 * element; both patterns are anchored so those aliases are skipped and no
 * operation is listed twice.
 */
const TAG_ID = /^tag\/[^/]+$/;
const OPERATION_ID = /^tag\/[^/]+\/operation\/.+$/;

/**
 * READ_ATTEMPTS is how many animation frames the reader will wait for Redoc's
 * sections to appear. Redoc reports itself loaded once the document is parsed,
 * which is normally the same commit that paints the sections, but a frame or two
 * of slack costs nothing and avoids reading an empty tree on a slow machine.
 */
const READ_ATTEMPTS = 10;

/**
 * sectionLabel reads a section's rendered heading. Redoc heads a tag group with
 * the tag name and an operation with its summary; the trailing permalink anchor
 * inside the heading contributes no text, so the heading's text content is the
 * label as displayed.
 */
const sectionLabel = (element: Element): string =>
  element.querySelector("h1, h2")?.textContent?.trim() ?? "";

/**
 * readSpecSections builds the reference outline from the rendered document,
 * walking the sections in document order so the rail reads in specification
 * order. An operation is filed under the tag its id names, so a tag group Redoc
 * rendered without any operation still appears (as an empty group) rather than
 * silently swallowing its heading.
 */
export const readSpecSections = (root: ParentNode): SpecSection[] => {
  const sections: SpecSection[] = [];
  const byTagId = new Map<string, SpecSection>();

  for (const element of root.querySelectorAll("[data-section-id]")) {
    const id = element.getAttribute("data-section-id") ?? "";

    if (TAG_ID.test(id)) {
      const section: SpecSection = { id, label: sectionLabel(element), operations: [] };
      byTagId.set(id, section);
      sections.push(section);
      continue;
    }

    if (!OPERATION_ID.test(id)) {
      continue;
    }

    const owner = byTagId.get(id.slice(0, id.indexOf("/operation/")));
    if (owner) {
      owner.operations.push({
        id,
        label: sectionLabel(element),
        method: element.querySelector(".http-verb")?.textContent?.trim() ?? "",
      });
    }
  }

  return sections.filter((section) => section.operations.length > 0);
};

/**
 * readSpecSectionsWhenReady polls across animation frames until the reference has
 * sections to read, then hands them to `onRead` once. It returns a cancel
 * function so a component that unmounts mid-poll stops the loop. Redoc's loaded
 * callback fires on the commit that paints the document, so the first read
 * usually succeeds and the loop never runs a second time.
 */
export const readSpecSectionsWhenReady = (
  root: ParentNode | null,
  onRead: (sections: SpecSection[]) => void,
): (() => void) => {
  if (!root) {
    return () => {};
  }

  let frame = 0;
  let attempts = 0;

  const attempt = (): void => {
    const sections = readSpecSections(root);

    if (sections.length > 0) {
      onRead(sections);

      return;
    }

    attempts += 1;

    if (attempts < READ_ATTEMPTS) {
      frame = window.requestAnimationFrame(attempt);
    }
  };

  attempt();

  return () => window.cancelAnimationFrame(frame);
};
